import { GATEWAY_BY_CODE } from 'constants/TransactionGateways';
import { chain } from 'lodash';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { LoadOptions } from 'react-select-async-paginate';
import { GlobalTypes } from 'redux/types';
import { getCompanyCredentialList } from 'services/API/Company';

// O(1) lookup: gateway_code → canonical display name for dropdown labels and grouping
const subNameByCode: Record<string, string> = chain(GATEWAY_BY_CODE)
  .mapValues('gateway_sub_name')
  .value();

export const valueToLabel = (value: any) => {
  if (!value) {
    return { label: '', value: null };
  }
  value.toString = () => value.id;
  return {
    label: subNameByCode[value.gateway_code] ?? value.gateway_sub_name,
    value: value,
  };
};

// Merge credentials that share a display name into one option with concatenated codes
const mergeGroup = (group: any[]) => ({
  ...group[0],
  id: chain(group).map('id').uniq().join(',').value(),
  gateway_code: chain(group).map('gateway_code').uniq().join(',').value(),
  gateway_sub_name: subNameByCode[group[0].gateway_code] ?? group[0].gateway_sub_name,
});

export const useCredentialOptions = (filterOptions: Record<string, any> = {}) => {
  const { user } = useSelector((state: GlobalTypes.RootState) => state.auth);

  const getCredentialOptions: LoadOptions<any, any> = useCallback(async () => {
    const result = await getCompanyCredentialList(user?.company_id, filterOptions);
    const hasMore = !!result?.meta?.pagination?.links?.next;

    const gateways = result.data.filter((item) => {
      if (filterOptions.type == 'preAuth') {
        return item.gateway_id != 2;
      }
      // if gateway_id is 9, search result includes UnionPay ACQ UPOP and QR
      // if gateway_id is 10, search result includes UnionPay ACQ C2B and B2C
      // if gateway_id is 11, search result includes UnionPay ACQ POS
      return item.gateway_id != 2 && item.gateway_id != 16;
    });

    return {
      options: chain(gateways)
        .groupBy((item) => subNameByCode[item.gateway_code] ?? item.gateway_sub_name)
        .map(mergeGroup)
        .map(valueToLabel)
        .value(),
      hasMore,
    };
  }, [user?.company_id, filterOptions]);

  return { getCredentialOptions };
};
